/**
 * Call It In LLC — Analytics Dashboard
 * - Dynamic data pulling from ci_tasks (localStorage)
 * - Integrated ComplexStatisticsCards and ReportsLineCharts
 */

import React, { useMemo } from "react";

// Template Components
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

export default function Analytics() {
  // 1. Hook into your real task data
  const tasks = useMemo(() => {
    try {
      const raw = localStorage.getItem("ci_tasks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  }, []);

  // 2. Logic to calculate "Crisp" stats
  const stats = useMemo(() => {
    const today = new Date().toLocaleDateString();
    const todayTasks = tasks.filter(t => new Date(t.createdAt).toLocaleDateString() === today);
    
    // Revenue logic based on your calcPrice previews
    const dailyRevenue = todayTasks.reduce((sum, t) => sum + (Number(t.preview?.total) || 0), 0);
    const activeFleet = tasks.filter(t => t.status !== "Delivered").length;
    const completedToday = todayTasks.filter(t => t.status === "Delivered").length;

    return { 
      total: tasks.length, 
      revenue: dailyRevenue, 
      active: activeFleet, 
      completed: completedToday 
    };
  }, [tasks]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Top Row: The Fleet Pulse */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <ComplexStatisticsCard
              color="dark"
              icon="assignment"
              title="Total Bookings"
              count={stats.total}
              percentage={{ color: "success", amount: "", label: "All-time volume" }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <ComplexStatisticsCard
              icon="leaderboard"
              title="Today's Revenue"
              count={`$${stats.revenue.toFixed(2)}`}
              color="info"
              percentage={{ color: "success", amount: "", label: "Live from estimates" }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <ComplexStatisticsCard
              color="success"
              icon="local_shipping"
              title="Active Fleet"
              count={stats.active}
              percentage={{ color: "success", amount: "", label: "Drivers on task" }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <ComplexStatisticsCard
              color="primary"
              icon="person_add"
              title="Completed Today"
              count={stats.completed}
              percentage={{ color: "success", amount: "", label: "Successful drops" }}
            />
          </Grid>
        </Grid>

        {/* Middle Row: Performance Trends */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="info"
                  title="Daily Load Volume"
                  description="Tasks processed per day"
                  date="updated just now"
                  chart={{
                    labels: ["M", "T", "W", "T", "F", "S", "S"],
                    datasets: { label: "Tasks", data: [5, 12, 8, 15, 20, 4, 2] }, // We can make this dynamic next!
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="success"
                  title="Revenue Trend"
                  description="Weekly earnings growth"
                  date="updated 4 min ago"
                  chart={{
                    labels: ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
                    datasets: { label: "Revenue", data: [450, 600, 300, 550, 800, 700, 1100, 950, 1200] },
                  }}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
